import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ArrowLeft, Pencil, CopyPlus, Boxes, PackagePlus, SlidersHorizontal, ShieldAlert,
  CreditCard, Monitor, Package, Cable, Link2, Paperclip, Megaphone, Wrench,
} from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import { money, CATEGORY_LABEL, CATEGORY_CHIP, formFactorLabel, type DeviceModelRow, type DeviceUnitRow } from '../../services/hardwareApi';
import type { BillingSkuRow } from '../../services/billingApi';

/**
 * DEVICE QUICK VIEW — the popup a Stock row opens on click. Basic info at a
 * glance (identity, cost, stock buckets, vendor, offers) with jump links to
 * every operation (edit, duplicate, units, receiving, adjustments, damage,
 * new offer). "Works with" devices are CLICKABLE and swap the popup to that
 * device — a small in-popup navigation stack with a back button, so an
 * operator can walk a terminal's ecosystem without leaving the page.
 *
 * Read-only by design: every write goes through the existing surfaces
 * (DeviceEditor, AdjustStockModal, the wizard) — this popup only routes.
 */

const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor, payment_device: CreditCard, peripheral: Package, accessory: Cable,
  office_supply: Paperclip, marketing_material: Megaphone, tools: Wrench,
};

const DEPLOYED = new Set(['shipped', 'delivered', 'deployed']);
const HOLD = new Set(['on_hold', 'backordered', 'rma', 'returned', 'retired']);
const PROVISIONING = new Set(['provisioning', 'configured']);

const Fact: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
  <div className="min-w-0">
    <div className="text-[9.5px] font-semibold uppercase tracking-wide text-stone-400">{label}</div>
    <div className={`truncate text-xs text-stone-800 ${mono ? 'font-mono' : ''}`}>{children}</div>
  </div>
);

const DeviceQuickView: React.FC<{
  modelId: string;
  models: DeviceModelRow[];
  units: DeviceUnitRow[];
  skus: BillingSkuRow[];
  stockByModel: Record<string, { onHand: number; reserved?: number } | undefined>;
  onClose: () => void;
  onEdit: (modelId: string) => void;
  onDuplicate: (modelId: string) => void;
  onViewUnits: (modelId: string) => void;
}> = ({ modelId, models, units, skus, stockByModel, onClose, onEdit, onDuplicate, onViewUnits }) => {
  const navigate = useNavigate();
  // In-popup navigation: clicking a supported device pushes it; back pops.
  const [stack, setStack] = useState<string[]>([modelId]);
  const currentId = stack[stack.length - 1];

  const modelById = useMemo(() => new Map(models.map((mm) => [mm.modelId, mm])), [models]);
  const m = modelById.get(currentId);

  if (!m) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="rounded-2xl bg-white p-6 text-sm text-stone-600" onClick={(e) => e.stopPropagation()}>
          Device not found. <button onClick={onClose} className="ml-2 font-semibold text-red-500">Close</button>
        </div>
      </div>
    );
  }

  const quantity = m.trackingMode === 'quantity';
  const modelUnitsAll = units.filter((u) => u.modelId === m.modelId);
  const qStock = stockByModel[m.modelId];
  const inStock = quantity ? (qStock?.onHand ?? 0) : modelUnitsAll.filter((u) => u.state === 'in_stock').length;
  const allocated = quantity ? (qStock?.reserved ?? 0) : modelUnitsAll.filter((u) => u.state === 'allocated').length;
  const provisioning = modelUnitsAll.filter((u) => PROVISIONING.has(u.state)).length;
  const deployed = modelUnitsAll.filter((u) => DEPLOYED.has(u.state)).length;
  const rma = modelUnitsAll.filter((u) => HOLD.has(u.state)).length;
  const offers = skus.filter((s) => s.modelId === m.modelId);

  const worksWith = (m.compatibility?.worksWith || []).map((id) => modelById.get(id)).filter(Boolean) as DeviceModelRow[];
  // The reverse edge: which hosts declare THIS device attachable — how an
  // accessory finds its terminals.
  const usedBy = models.filter((x) => x.modelId !== m.modelId && (x.compatibility?.worksWith || []).includes(m.modelId));

  const open = (id: string) => setStack((prev) => [...prev, id]);
  const Icon = CATEGORY_ICON[m.category] || Package;

  const DeviceLink: React.FC<{ d: DeviceModelRow }> = ({ d }) => {
    const DIcon = CATEGORY_ICON[d.category] || Package;
    return (
      <button
        onClick={() => open(d.modelId)}
        className="flex w-full items-center gap-2 rounded-lg border border-stone-200 bg-white px-2.5 py-2 text-left hover:border-stone-300 hover:bg-stone-50"
      >
        <DIcon size={14} className="shrink-0 text-stone-400" />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-stone-800">{d.modelName}</span>
        <span className="shrink-0 font-mono text-[10px] text-stone-400">{money(d.landedCostCents)}</span>
        <Link2 size={11} className="shrink-0 text-stone-300" />
      </button>
    );
  };

  const jump = (to: string) => { onClose(); navigate(to); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-stone-200 bg-stone-50 px-5 py-4">
          {stack.length > 1 && (
            <button
              onClick={() => setStack((prev) => prev.slice(0, -1))}
              className="rounded-lg border border-stone-300 bg-white p-1.5 text-stone-500 hover:text-stone-800"
              title="Back"
            >
              <ArrowLeft size={14} />
            </button>
          )}
          <ShimmerImage
            src={m.media?.heroImageUrl}
            alt={m.modelName}
            width={52}
            height={52}
            objectFit="contain"
            className="shrink-0 rounded-xl"
            fallbackBgColor="bg-stone-900"
            fallbackIcon={<Icon size={22} className="text-stone-400" />}
            variant="dark"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-stone-900">{m.modelName}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${CATEGORY_CHIP[m.category] || 'bg-stone-100 text-stone-600'}`}>
                {CATEGORY_LABEL[m.category] || m.category}
              </span>
              {m.brand && <span className="rounded bg-stone-200 px-1.5 py-0.5 text-[10px] font-semibold text-stone-700">{m.brand}</span>}
              <span className="text-[11px] text-stone-500">{m.manufacturer}</span>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-stone-400 hover:bg-stone-200 hover:text-stone-700"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Facts */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-stone-200 p-3.5 md:grid-cols-4">
            <Fact label="Device ID" mono>{m.modelId}</Fact>
            <Fact label="Internal barcode" mono>{m.sharedBarcode || '—'}</Fact>
            <Fact label="Landed cost" mono>{money(m.landedCostCents)}</Fact>
            <Fact label="Tracking">{quantity ? 'Total inventory' : 'Individual units'}</Fact>
            <Fact label="Vendor">{m.vendor?.supplierName || '—'}</Fact>
            <Fact label="Lead time">{m.vendor?.leadTimeDays ? `${m.vendor.leadTimeDays} days` : '—'}</Fact>
            <Fact label="Form factor">{formFactorLabel(m.appProfile?.terminalFormFactor)}</Fact>
            <Fact label="Warranty">{m.lifecycle?.warrantyMonths ? `${m.lifecycle.warrantyMonths} mo` : '—'}</Fact>
          </div>

          {/* Stock buckets */}
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
            {[
              ['In stock', inStock, inStock > 0 ? 'text-stone-900' : 'text-stone-400'],
              ['Allocated', allocated, allocated > 0 ? 'text-stone-900' : 'text-stone-400'],
              ...(quantity ? [] : [
                ['Provisioning', provisioning, provisioning > 0 ? 'text-rose-700' : 'text-stone-400'],
                ['Deployed', deployed, deployed > 0 ? 'text-stone-900' : 'text-stone-400'],
                ['RMA / hold', rma, rma > 0 ? 'text-red-600' : 'text-stone-400'],
              ] as [string, number, string][]),
              ['Offers', offers.length, offers.length > 0 ? 'text-emerald-700' : 'text-amber-600'],
            ].map(([label, n, cls]) => (
              <div key={label as string}>
                <span className={`font-mono text-sm font-bold ${cls}`}>{n as number}</span>
                <span className="ml-1.5 text-[10.5px] text-stone-500">{label}</span>
              </div>
            ))}
          </div>

          {m.description && <p className="mt-3 text-xs leading-relaxed text-stone-600">{m.description}</p>}

          {/* Operations */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => onEdit(m.modelId)} className="qv-btn"><Pencil size={12} /> Edit</button>
            <button onClick={() => onDuplicate(m.modelId)} className="qv-btn"><CopyPlus size={12} /> Duplicate</button>
            <button onClick={() => onViewUnits(m.modelId)} className="qv-btn"><Boxes size={12} /> {quantity ? 'Stock ledger' : 'Units'}</button>
            <button onClick={() => jump('/inventory/receiving')} className="qv-btn"><PackagePlus size={12} /> Receive</button>
            <button onClick={() => jump('/inventory/adjustments')} className="qv-btn"><SlidersHorizontal size={12} /> Adjust</button>
            <button onClick={() => jump('/inventory/damage')} className="qv-btn"><ShieldAlert size={12} /> Damage</button>
            <button
              onClick={() => jump(`/inventory/financing/new?target=model:${encodeURIComponent(m.modelId)}`)}
              className="qv-btn text-emerald-700"
            >
              <CreditCard size={12} /> New offer
            </button>
          </div>

          {/* Ecosystem */}
          {(worksWith.length > 0 || usedBy.length > 0) && (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {worksWith.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-stone-400">Supported devices</div>
                  <div className="space-y-1.5">{worksWith.map((d) => <DeviceLink key={d.modelId} d={d} />)}</div>
                </div>
              )}
              {usedBy.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-stone-400">Attaches to</div>
                  <div className="space-y-1.5">{usedBy.map((d) => <DeviceLink key={d.modelId} d={d} />)}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <style>{`
          .qv-btn { display:inline-flex; align-items:center; gap:5px; border:1px solid #d6d3d1; background:#fff;
            color:#44403c; border-radius:8px; padding:6px 11px; font-size:11px; font-weight:600; cursor:pointer; }
          .qv-btn:hover { background:#fafaf9; border-color:#a8a29e; }
        `}</style>
      </div>
    </div>
  );
};

export default DeviceQuickView;
